import { useEffect, useState } from "react";
import { PiFarmFill } from "react-icons/pi";
import AjudaTela from "../../components/AjudaTela";
import Layout from "../../components/Layout";
import { repositorioFazenda } from "../../sync/repositories/fazendas";

const AJUDA = {
  titulo: "Cadastro de Fazendas",
  descricao:
    "Fazenda é a unidade produtiva principal do sistema — cada fazenda cadastrada aqui é a base para os demais cadastros (Talhões, Máquinas, Funcionários, Culturas, Safras e Atividades), já que tudo no sistema é organizado e separado por fazenda. Cadastre aqui os dados básicos de cada propriedade que você administra.",
  secoes: [
    {
      titulo: "Dados da Fazenda",
      campos: [
        {
          nome: "Nome da Fazenda",
          obrigatorio: true,
          explicacao:
            "Nome que identifica a propriedade no sistema. É esse nome que aparecerá nas listas de seleção de fazenda em Talhões, Máquinas, Funcionários e demais telas.",
        },
        {
          nome: "Hectares",
          obrigatorio: true,
          explicacao:
            "Área total da fazenda, em hectares. Serve como referência geral da propriedade — não precisa ser igual à soma das áreas dos talhões.",
        },
        {
          nome: "Estado",
          obrigatorio: true,
          explicacao:
            "Unidade Federativa (UF) onde a fazenda está localizada. É usado para carregar automaticamente a lista de cidades no campo Cidade.",
        },
        {
          nome: "Cidade",
          obrigatorio: true,
          explicacao:
            "Município onde a fazenda está localizada. Só pode ser preenchido depois de selecionar o Estado — a lista de cidades vem do IBGE.",
        },
        {
          nome: "Atividade Principal",
          obrigatorio: true,
          explicacao:
            "Indica se a fazenda trabalha com Agricultura, Pecuária ou Ambos. Ajuda a identificar o perfil da propriedade nas listagens.",
        },
        {
          nome: "Latitude / Longitude",
          obrigatorio: false,
          explicacao:
            "Coordenadas geográficas da fazenda, usadas para localizar a propriedade em mapas. Preencha apenas se tiver essa informação em mãos.",
        },
      ],
    },
  ],
  dicas: [
    "Excluir uma fazenda com talhões, máquinas, funcionários ou outros registros vinculados não é permitido — remova ou realoque esses vínculos antes de excluir.",
    "A busca de cidades depende de internet, pois consulta a lista de municípios do IBGE a partir do Estado selecionado.",
  ],
};

export default function GestaoFazendas() {
  const [loading, setLoading] = useState(false);
  const [carregandoLista, setCarregandoLista] = useState(true);
  const [fazendas, setFazendas] = useState([]);
  const [fazendasFiltradas, setFazendasFiltradas] = useState([]);
  const [editando, setEditando] = useState(false);
  const [editandoId, setEditandoId] = useState(null);
  const [mostrarFormulario, setMostrarFormulario] = useState(false);
  const [opcoesAtividade, setOpcoesAtividade] = useState([]);
  const [carregandoOpcoes, setCarregandoOpcoes] = useState(true);
  const [cidades, setCidades] = useState([]);
  const [cidadesFiltradas, setCidadesFiltradas] = useState([]);
  const [showListaCidades, setShowListaCidades] = useState(false);
  
  const [filtros, setFiltros] = useState({
    busca: ""
  });
  
  const [formData, setFormData] = useState({
    nome: "",
    hectares: "",
    atividade_principal: "",
    cidade: "",
    estado: "",
    latitude: "",
    longitude: "",
  });

  const estados = [
    "AC", "AL", "AP", "AM", "BA", "CE", "DF", "ES", "GO", "MA",
    "MT", "MS", "MG", "PA", "PB", "PR", "PE", "PI", "RJ", "RN",
    "RS", "RO", "RR", "SC", "SP", "SE", "TO"
  ];

  // Opções de atividade principal (lista fixa — não há endpoint de opções no backend)
  useEffect(() => {
    setOpcoesAtividade([
      { value: "AGRICULTURA", label: "Agricultura" },
      { value: "PECUARIA", label: "Pecuária" },
      { value: "AMBOS", label: "Ambos" },
    ]);
    setCarregandoOpcoes(false);
  }, []);

  // Carregar fazendas
  useEffect(() => {
    carregarFazendas();
  }, []);

  // Buscar cidades quando estado mudar
  useEffect(() => {
    if (formData.estado) {
      fetch(`https://brasilapi.com.br/api/ibge/municipios/v1/${formData.estado}`)
        .then((res) => res.json())
        .then((data) => {
          const nomes = data.map((c) => c.nome);
          setCidades(nomes);
          setCidadesFiltradas(nomes);
        })
        .catch((err) => console.error("Erro ao buscar cidades:", err));
    } else {
      setCidades([]);
      setCidadesFiltradas([]);
    }
  }, [formData.estado]);

  const carregarFazendas = async () => {
    try {
      setCarregandoLista(true);
      const response = await repositorioFazenda.listar();

      const dados = response.data.results || response.data;
      setFazendas(dados);
      setFazendasFiltradas(dados);
    } catch (error) {
      console.error("Erro ao carregar fazendas:", error);
      alert("Erro ao carregar lista de fazendas");
    } finally {
      setCarregandoLista(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleLatLongChange = (e) => {
    const { name, value } = e.target;
    let cleanValue = value.replace(/[^\d.-]/g, "");
    if (cleanValue.length > 2 && !cleanValue.includes(".")) {
      cleanValue = cleanValue.slice(0, 2) + "." + cleanValue.slice(2);
    }
    setFormData(prev => ({ ...prev, [name]: cleanValue }));
  };

  const handleCidadeChange = (e) => {
    const valor = e.target.value;
    setFormData(prev => ({ ...prev, cidade: valor }));
    setShowListaCidades(true);
    setCidadesFiltradas(cidades.filter((c) => c.toLowerCase().includes(valor.toLowerCase())));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.nome.trim()) {
      alert("Preencha o nome da fazenda.");
      return;
    }
    if (!formData.estado) {
      alert("Selecione o estado.");
      return;
    }
    if (!formData.cidade.trim()) {
      alert("Preencha a cidade.");
      return;
    }
    
    setLoading(true);

    const payload = {
      ...formData,
      hectares: formData.hectares ? Number(formData.hectares) : null,
      latitude: formData.latitude ? Number(formData.latitude) : null,
      longitude: formData.longitude ? Number(formData.longitude) : null,
    };

    try {
      if (editando) {
        // Editar fazenda existente
        const response = await repositorioFazenda.atualizar(editandoId, payload);
        if (response.status === 200) {
          alert("Fazenda atualizada com sucesso!");
          handleCancel();
          carregarFazendas();
        }
      } else {
        // Cadastrar nova fazenda
        const response = await repositorioFazenda.criar(payload);
        if (response.status === 201 || response.status === 200) {
          alert("Fazenda cadastrada com sucesso!");
          handleCancel();
          carregarFazendas();
        }
      }
    } catch (error) {
      console.error("Erro:", error);
      const data = error.response?.data;
      if (data && typeof data === 'object' && !Array.isArray(data)) {
        const erros = Object.values(data).flat();
        alert(erros.join("\n"));
      } else if (Array.isArray(data)) {
        alert(data.join("\n"));
      } else {
        alert("Erro ao salvar. Verifique os dados e tente novamente.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEditar = (fazenda) => {
    setFormData({
      nome: fazenda.nome || "",
      hectares: fazenda.hectares || "",
      atividade_principal: fazenda.atividade_principal || "",
      cidade: fazenda.cidade || "",
      estado: fazenda.estado || "",
      latitude: fazenda.latitude || "",
      longitude: fazenda.longitude || "",
    });
    setEditando(true);
    setEditandoId(fazenda.id);
    setMostrarFormulario(true);
    
    // Scroll para o topo para ver o formulário
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  const handleNovaFazenda = () => {
    handleCancel();
    setMostrarFormulario(true);
  };

  const handleCancel = () => {
    setFormData({
      nome: "",
      hectares: "",
      atividade_principal: "",
      cidade: "",
      estado: "",
      latitude: "",
      longitude: "",
    });
    setEditando(false);
    setEditandoId(null);
    setMostrarFormulario(false);
    setShowListaCidades(false);
  };

  const handleExcluir = async (id) => {
    if (!window.confirm("Tem certeza que deseja excluir esta fazenda? Esta ação não pode ser desfeita.")) {
      return;
    }
    
    try {
      await repositorioFazenda.excluir(id);
      alert("Fazenda excluída com sucesso!");
      carregarFazendas();
      
      // Se estava editando esta fazenda, limpar formulário
      if (editandoId === id) {
        handleCancel();
      }
    } catch (error) {
      console.error("Erro ao excluir:", error);
      alert("Erro ao excluir fazenda. Verifique se não há registros vinculados.");
    }
  };

  // Aplicar filtros
  const aplicarFiltros = () => {
    let filtrados = [...fazendas];
    
    if (filtros.busca) {
      const buscaLower = filtros.busca.toLowerCase();
      filtrados = filtrados.filter(f => 
        f.nome?.toLowerCase().includes(buscaLower) ||
        f.cidade?.toLowerCase().includes(buscaLower) ||
        f.estado?.toLowerCase().includes(buscaLower)
      );
    }
    
    setFazendasFiltradas(filtrados);
  };

  // Limpar filtros
  const limparFiltros = () => {
    setFiltros({ busca: "" });
    setFazendasFiltradas(fazendas);
  };

  // Formatar hectares
  const formatarHectares = (hectares) => {
    if (!hectares) return "—";
    return `${hectares} ha`;
  };

  return (
    <Layout>
      <div className="p-6 font-medium max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center gap-2">
            <h2 className="text-3xl font-bold text-gray-800 flex items-center gap-2">Gestão de Fazendas <PiFarmFill size={38} /></h2>
            <AjudaTela {...AJUDA} />
          </div>
          {!mostrarFormulario && (
            <button
              onClick={handleNovaFazenda}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors flex items-center gap-2 shadow-md"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Nova Fazenda
            </button>
          )}
        </div>
        
        {/* Formulário de Cadastro/Edição */}
        {mostrarFormulario && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-8 border border-gray-200">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-gray-800">
                {editando ? "Editar Fazenda" : "Cadastrar Nova Fazenda"}
              </h3>
              <button
                onClick={handleCancel}
                className="text-gray-500 hover:text-gray-700"
                title="Fechar formulário"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                <div className="flex gap-4 flex-wrap">
                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-1">Nome da Fazenda *</label>
                    <input
                      type="text"
                      name="nome"
                      value={formData.nome}
                      onChange={handleChange}
                      className="w-80 border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                      placeholder="Digite o nome da fazenda..."
                      required
                    />
                  </div>

                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-1">Hectares *</label>
                    <div className="flex items-center border border-green-500 rounded">
                      <input
                        type="number"
                        name="hectares"
                        value={formData.hectares}
                        onChange={handleChange}
                        className="w-28 p-2 outline-none rounded-l focus:ring-2 focus:ring-green-300"
                        required
                        step="0.01"
                        min="0"
                      />
                      <div className="h-6 w-px bg-green-500" />
                      <span className="px-3 text-gray-700 bg-gray-100 rounded-r">ha</span>
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap gap-4 relative">
                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-1">Latitude / Longitude</label>
                    <div className="flex items-center border border-green-500 rounded">
                      <input
                        type="text"
                        name="latitude"
                        value={formData.latitude}
                        onChange={handleLatLongChange}
                        placeholder="-22.123456"
                        className="w-32 p-2 outline-none rounded-l focus:ring-2 focus:ring-green-300"
                      />
                      <div className="h-6 w-px bg-green-500" />
                      <input
                        type="text"
                        name="longitude"
                        value={formData.longitude}
                        onChange={handleLatLongChange}
                        placeholder="-51.123456"
                        className="w-32 p-2 outline-none rounded-r focus:ring-2 focus:ring-green-300"
                      />
                    </div>
                  </div>

                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-1">Estado *</label>
                    <select
                      name="estado"
                      value={formData.estado}
                      onChange={handleChange}
                      className="w-32 border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                      required
                    >
                      <option value="">UF</option>
                      {estados.map((sigla) => (
                        <option key={sigla} value={sigla}>{sigla}</option>
                      ))}
                    </select>
                  </div>

                  <div className="flex flex-col relative">
                    <label className="text-sm font-medium text-gray-700 mb-1">Cidade *</label>
                    <input
                      type="text"
                      name="cidade"
                      value={formData.cidade}
                      onChange={handleCidadeChange}
                      onFocus={() => setShowListaCidades(true)}
                      placeholder={formData.estado ? "Digite a cidade..." : "Selecione o estado primeiro"}
                      disabled={!formData.estado}
                      className="w-64 border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                      required
                    />
                    {showListaCidades && formData.cidade && cidadesFiltradas.length > 0 && (
                      <ul className="absolute top-full left-0 right-0 bg-white border rounded mt-1 max-h-48 overflow-y-auto shadow-lg z-10">
                        {cidadesFiltradas.map((nome) => (
                          <li
                            key={nome}
                            onClick={() => {
                              setFormData({ ...formData, cidade: nome });
                              setShowListaCidades(false);
                            }}
                            className="px-3 py-2 hover:bg-green-100 cursor-pointer"
                          >
                            {nome}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>

                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-1">Atividade Principal *</label>
                  {carregandoOpcoes ? (
                    <p className="text-gray-500">Carregando opções...</p>
                  ) : (
                    <select
                      name="atividade_principal"
                      value={formData.atividade_principal}
                      onChange={handleChange}
                      className="w-64 border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                      required
                    >
                      <option value="">Selecione...</option>
                      {opcoesAtividade.map((opcao) => (
                        <option key={opcao.value} value={opcao.value}>
                          {opcao.label}
                        </option>
                      ))}
                    </select>
                  )}
                </div>

                <div className="flex gap-4 pt-4">
                  <button
                    type="submit"
                    disabled={loading}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors disabled:bg-blue-300"
                  >
                    {loading ? "Salvando..." : (editando ? "Atualizar" : "Cadastrar")}
                  </button>
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg transition-colors"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            </form>
          </div>
        )}
        
        {!mostrarFormulario && (
          <>
            {/* Seção de Filtros */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-8 border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-700 mb-4 flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                Filtros de Busca
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-1">Buscar por Nome, Cidade ou Estado</label>
                  <input
                    type="text"
                    placeholder="Digite o nome, cidade ou estado da fazenda..."
                    value={filtros.busca}
                    onChange={(e) => setFiltros({...filtros, busca: e.target.value})}
                    className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                  />
                </div>
              </div>
              
              <div className="flex gap-3 mt-4">
                <button
                  onClick={aplicarFiltros}
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded transition-colors"
                >
                  Aplicar Filtros
                </button>
                <button
                  onClick={limparFiltros}
                  className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded transition-colors"
                >
                  Limpar Filtros
                </button>
              </div>
            </div>

            {/* Tabela de Fazendas */}
            <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
              <div className="p-4 border-b border-gray-200 bg-gray-50">
                <p className="text-gray-600">
                  {fazendasFiltradas.length} fazenda(s) encontrada(s)
                </p>
              </div>
              
              {carregandoLista ? (
                <div className="p-8 text-center text-gray-500">Carregando...</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Código</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Nome</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Hectares</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Cidade</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Estado</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Atividade</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {fazendasFiltradas.map((fazenda) => (
                        <tr key={fazenda.id} className="border-t border-gray-200 hover:bg-gray-50 transition-colors">
                          <td className="p-3 text-sm font-mono">{fazenda.id}</td>
                          <td className="p-3 text-sm font-medium">{fazenda.nome}</td>
                          <td className="p-3 text-sm">{formatarHectares(fazenda.hectares)}</td>
                          <td className="p-3 text-sm">{fazenda.cidade || "—"}</td>
                          <td className="p-3 text-sm">{fazenda.estado || "—"}</td>
                          <td className="p-3 text-sm">{fazenda.atividade_principal || "—"}</td>
                          <td className="p-3">
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleEditar(fazenda)}
                                className="bg-yellow-500 hover:bg-yellow-600 text-white px-3 py-1 rounded text-sm transition-colors"
                                title="Editar"
                              >
                                Editar
                              </button>
                              <button
                                onClick={() => handleExcluir(fazenda.id)}
                                className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-sm transition-colors"
                                title="Excluir"
                              >
                                Excluir
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      
                      {fazendasFiltradas.length === 0 && (
                        <tr>
                          <td colSpan="7" className="p-8 text-center text-gray-500">
                            {filtros.busca 
                              ? "Nenhuma fazenda encontrada com os filtros selecionados."
                              : "Nenhuma fazenda cadastrada."}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}